export type ColorScheme = 'dark' | 'light'
export const fieldWidthWeb = 240

//#region colors
export const white = '#FFFFFF'
export const black = '#000000'
export const nearWhite = '#EEEEEE'
export const lightGrey = '#CCCCCC'
export const middleGrey = '#BBBBBB'
export const midGrey = '#888888'
export const middleDarkGrey = '#777777'

export const pasteboardBG = '#525659'

const grey = {
  0: '#000000',
  1: '#111111',
  2: '#222222',
  3: '#333333',
  4: '#444444',
  5: '#555555',
  6: '#666666',
  7: '#777777',
  8: '#888888',
  9: '#999999',
  A: '#AAAAAA',
  B: '#BBBBBB',
  C: '#CCCCCC',
  D: '#DDDDDD',
  E: '#EEEEEE',
  F: '#FFFFFF',
}

// Used for making the header and other elements look just like native iOS
// const appleBackdrop = '#FFF'
// const appleBackdropDark = '#000'
const appleCanvas = '#F5F5F5'
const appleCanvasBorder = '#E9E9E9' // TODO: double-check
const appleCanvasDark = '#131313'
const appleCanvasBorderDark = '#131313'
// const applePaper = '#1C1C1C'
// const applePaperBorder = '#1C1C1C'
// const applePaperOpaqueDark = '#404040'
// const applePaperOpaqueColorDark = '#FFF'
// const applePaperSeparator = '#252525'
// const appleSeparator = '#CBCBCB'
// const appleSeparatorDark = '#2D2D2D' // TODO: find exact value
const appleBlue = '#0071EB'
const appleRed = '#EB0C00'
const appleYellowFill = '#FFEC7F'
// const appleYellowGlyph = '#F7CE46'

// UPS Theme
const upsGrey = '#EEEEF1'
/**
 * upsGrey + 0.5 opacity
 */
const upsGreyHalf = '#F7F7F7'
const upsBorder = '#E5E4EC'
// const upsBGDark = '#2B2B2B'
// const upsBGSecDark = '#2E2A27'
export const upsLeaf = '#ACF163'
// const upsBrandDark = '#2D5007'
const upsBorderRad = 16
const upsPurple = '#7C3BEC'
const upsDarkGrey = '#2E2A27'
const upsFontFamily = 'DM Sans'

// Glow colors from website

const glowSand = '#FAF7EB'
const glowLeaf = '#63D370'
const glowSun = '#EFA068'
const glowTreeBark = '#210102'
// Secondary button border and font
export const glowLightHover = '#EAE6DB'
// const glowPlatinum = '#EBE9E8'
export const glowStoneGrey = '#CBCAC9'
// const glowGravelBrown = '#534F4B'
const glowWetSand = '#CFC6BC'
export const glowSeparatorDark = 'turquoise'
const glowBorderRad = 32

// const glowText = glowDark
// const glowTextDark = glowLight

export const glowThird = '#EBE9E8'

// const glowSubContentLight = '#CBCAC9'
// const glowThird = '#EBE9E8'

//#endregion colors
//#region types
export const surfaceTypes = [
  'backdrop',
  'canvas',
  'paper',
  'pasteboard',
] as const
export type Surface = (typeof surfaceTypes)[number]

export type OpaqueColorValue = symbol & { __TYPE__: 'Color' }
export type ColorValue = string | OpaqueColorValue

export type Gap = 2 | 4 | 8 | 10 | 12 | 16 | 24 | 28 | 32

export interface Layer {
  readonly backgroundColor?: ColorValue
  readonly borderColor?: ColorValue
  readonly borderRadius?: number
  readonly borderTopColor?: ColorValue
  readonly borderTopWidth?: number
  readonly borderWidth?: number
  readonly color?: ColorValue
  readonly disabled?: string
  readonly faint?: string
  readonly faint__color?: string
  // readonly focus?: Layer
  readonly fontFamily?: string
  readonly fontWeight?:
    | 'normal'
    | 'bold'
    | '100'
    | '200'
    | '300'
    | '400'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900'
  readonly fontSize?: number
  readonly gap?: Gap
  readonly highlight?: string
  readonly highlight__color?: string
  // readonly hover?: Layer
  readonly hoverBC?: string
  readonly hoverBG?: string
  readonly hoverColor?: string
  readonly italic?: true
  readonly labelColor?: string
  readonly labelFontFamily?: string
  readonly labelFontSize?: number
  readonly labelItalic?: true
  readonly labelUppercase?: boolean
  readonly opaque?: string
  readonly opaque__color?: string
  readonly paddingHorizontal?: number
  readonly paddingVertical?: number
  readonly placeholderColor?: string
  readonly separator?: string
}

export type BGAwareLayer = Readonly<Record<Surface, Layer>>

export interface Theme {
  /** To be used sparingly. */
  readonly accent: string
  readonly backdrop: Layer
  readonly borderRadius?: number
  readonly button: BGAwareLayer
  readonly input: BGAwareLayer
  /** The color that most represents the company */
  readonly brand: string
  /** Text color to contrast against brand color */
  readonly brand__color: string
  /** Danger */
  readonly danger: string
  /** Main content holder */
  readonly canvas: Layer
  /** Font family for everything */
  readonly fontFamily?: string
  /** Sub content container */
  readonly paper: Layer
  /** The surface behind simulated sheets of paper */
  readonly pasteboard: Layer
  /** Links that open an external target or new window */
  readonly href: string
  /** Complementary color */
  readonly third: string
  /** Warning */
  readonly warning: string
}

interface ThemeTuple {
  readonly dark: Theme
  readonly light: Theme
}
//#endregion types
//#region default

const pasteboardLayer: Layer = {
  backgroundColor: pasteboardBG,
}
const defaultLayerDark: Layer = {
  backgroundColor: upsDarkGrey,
  borderColor: upsDarkGrey,
  borderRadius: 0,
  borderWidth: 0,
  color: white,
  disabled: middleDarkGrey,
  fontWeight: 'normal',
  highlight: appleBlue,
  highlight__color: white,
  opaque: middleGrey,
  opaque__color: black,
  separator: middleGrey,
}

const defaultThemeDark: Theme = {
  accent: appleBlue,
  backdrop: {
    backgroundColor: appleCanvasDark,
  },
  brand: appleCanvas,
  brand__color: white,
  button: {
    backdrop: { ...defaultLayerDark, borderColor: appleCanvasDark },
    canvas: { ...defaultLayerDark, borderColor: appleCanvasDark },
    paper: { ...defaultLayerDark, borderColor: appleCanvasDark },
    pasteboard: {},
  },
  canvas: defaultLayerDark,
  danger: appleRed,
  href: appleBlue,
  input: {
    backdrop: {},
    canvas: {
      ...defaultLayerDark,
      backgroundColor: appleCanvasDark,
      borderColor: appleCanvasDark,
    },
    paper: {
      ...defaultLayerDark,
      backgroundColor: appleCanvasDark,
      borderColor: appleCanvasDark,
    },
    pasteboard: {},
  },
  paper: defaultLayerDark,
  pasteboard: pasteboardLayer,
  third: appleYellowFill,
  warning: appleYellowFill,
}

const defaultInputLight: Layer = {
  backgroundColor: upsGrey,
  borderColor: upsGrey,
  borderRadius: upsBorderRad,
  borderWidth: 1,
  color: black,
  labelFontSize: 18,
}

const defaultLayerLight: Layer = {
  backgroundColor: white,
  borderColor: white,
  borderRadius: 0,
  borderWidth: 1,
  color: black,
  disabled: middleDarkGrey,
  faint: nearWhite,
  fontWeight: 'normal',
  highlight: appleBlue,
  highlight__color: white,
  opaque: middleGrey,
  opaque__color: black,
  placeholderColor: nearWhite,
  separator: middleGrey,
}

const defaultPaperLight: Layer = {
  ...defaultLayerLight,
  backgroundColor: upsGrey,
  borderColor: upsBorder,
}

const defaultThemeLight: Theme = {
  accent: appleBlue,
  backdrop: defaultLayerLight,
  brand: appleCanvasBorder,
  brand__color: black,
  button: {
    backdrop: {},
    canvas: {},
    paper: defaultPaperLight,
    pasteboard: {},
  },
  canvas: defaultLayerLight,
  danger: appleRed,
  href: appleBlue,
  input: {
    backdrop: {},
    canvas: defaultInputLight,
    paper: { ...defaultInputLight, backgroundColor: white },
    pasteboard: {},
  },
  paper: defaultPaperLight,
  pasteboard: pasteboardLayer,
  third: appleYellowFill,
  warning: appleYellowFill,
}
export const defaultThemeTuple: ThemeTuple = {
  dark: defaultThemeDark,
  light: defaultThemeLight,
}
//#endregion default
//#region glow
const glowPaperLight: Layer = {
  backgroundColor: glowSand,
  borderColor: glowSand,
  borderRadius: glowBorderRad,
  borderWidth: 1,
  color: glowTreeBark,
  fontFamily: 'Manrope',
  highlight: glowTreeBark,
  highlight__color: white,
  hoverBG: glowWetSand,
  placeholderColor: nearWhite,
  separator: glowWetSand,
  opaque: upsDarkGrey,
  opaque__color: middleGrey,
}
const glowPaperDark: Layer = {
  color: glowSand,
}
const glowDefaultInputLight: Layer = {
  ...glowPaperLight,
  backgroundColor: white,
  borderColor: white,
  color: glowTreeBark,
  paddingHorizontal: 12,
  paddingVertical: 18,
}

const glowBtnBase: Layer = {
  borderRadius: Number.MAX_SAFE_INTEGER,
  borderWidth: 1,
  fontFamily: 'Manrope',
}
//#endregion glow
//#region nirvana
const nirvanaPadV: Gap = 10
const nirvanaPadH = nirvanaPadV * 1.4

const nirvanaRad = 8

const nirvanaGap: Gap = 8
const nirvanaGapCanvas: Gap = 12

const nirvanaBlue = {
  /**
   * Nirvana's complementary third.
   */
  30: '#2277B5',
}
const nirvanaGray = {
  10: '#F8F8F8',
  30: '#E0E0E0',
  40: '#C6C6C6',
  50: '#A3A3A3',
  60: '#737373',
  70: '#525252',
}

const nirvanaGreen = {
  30: '#BFF230',
  40: '#A1C147',
  50: '#76b900',
  70: '#3F8500',
  90: '#265600',
}
/**
 * The logo's inner fill color.
 */
const nirvanaLogoYellow = '#b8a843'

const nirvanaHighlight = nirvanaLogoYellow

const nirvanaBtn: Layer = {
  backgroundColor: nirvanaGreen['50'],
  borderRadius: nirvanaRad,
  color: nirvanaGray['10'],
  faint: nirvanaGray['10'],
  faint__color: nirvanaGray['40'],
  fontFamily: 'Game',
  hoverBG: nirvanaGreen['30'],
  hoverColor: nirvanaGray['30'],
}

const nirvanaInputBase: Layer = {
  borderRadius: nirvanaRad,
  borderWidth: 0.5,
  fontFamily: 'Game',
  fontSize: 14,
  italic: true,
  labelFontFamily: 'Decimal',
  labelFontSize: 10,
  labelItalic: true,
  labelUppercase: true,
  paddingHorizontal: nirvanaPadH,
  paddingVertical: nirvanaPadV,
}

const nirvanaInputLight: Layer = {
  ...nirvanaInputBase,
  backgroundColor: nirvanaGray['30'],
  borderColor: nirvanaGray['40'],
  color: nirvanaGray['50'],
  disabled: nirvanaGray['10'],
  highlight: nirvanaHighlight,
  highlight__color: nirvanaGray['10'],
  labelColor: nirvanaGray['70'],
  placeholderColor: nirvanaGray['50'],
}

const nirvanaInputDark: Layer = {
  ...nirvanaInputBase,
  backgroundColor: '#2D2B26',
  borderColor: '#49453D',
  color: '#B2A68E',
  disabled: '#766F5F',
  highlight: nirvanaHighlight,
  // TODO
  highlight__color: '#FEF6D5',
  labelColor: '#C9BDA1',
  // TODO
  placeholderColor: '#C9BDA1',
}

const nirvanaInputBackdropDark: Layer = {
  ...nirvanaInputBase,
  color: white,
}
const nirvanaInputBackdropLight: Layer = {
  ...nirvanaInputBase,
  color: white,
}

const nirvanaThemeDark: Theme = {
  accent: appleRed, // ?
  backdrop: {
    ...defaultLayerDark,
    backgroundColor: nirvanaGray[70],
    borderRadius: nirvanaRad,
    color: white,
    fontFamily: 'Game',
    // For the breadcrumbs bg pretty much
    highlight: nirvanaLogoYellow,
    highlight__color: nirvanaGray['10'],
    gap: nirvanaGap,
  },
  borderRadius: nirvanaRad,
  brand: nirvanaLogoYellow,
  brand__color: white,
  button: {
    backdrop: defaultLayerDark,
    canvas: nirvanaBtn,
    paper: defaultLayerDark,
    pasteboard: {},
  },
  canvas: {
    backgroundColor: '#171613',
    borderRadius: nirvanaRad,
    color: '#C9BDA1',
    fontFamily: 'Game',
    gap: nirvanaGapCanvas,
    highlight: nirvanaHighlight,
    opaque: '#C9BDA1',
  },
  danger: appleRed,
  fontFamily: 'Play',
  href: nirvanaBlue['30'],
  input: {
    backdrop: nirvanaInputBackdropDark,
    canvas: { ...nirvanaInputDark, backgroundColor: '#2D2B25' },
    paper: nirvanaInputDark,
    pasteboard: {},
  },
  paper: {
    backgroundColor: '#2D2B26',
    borderRadius: nirvanaRad,
    highlight: nirvanaHighlight,
    color: '#C9BDA1',
    paddingHorizontal: nirvanaPadH,
    paddingVertical: nirvanaPadV,
    gap: nirvanaPadV,
  },
  pasteboard: pasteboardLayer,
  third: nirvanaBlue['30'],
  warning: nirvanaLogoYellow,
}

const nirvanaThemeLight: Theme = {
  accent: appleRed, // ?
  backdrop: {
    ...defaultLayerLight,
    backgroundColor: nirvanaGray['30'],
    borderRadius: nirvanaRad,
    color: nirvanaGray['70'],
    fontFamily: 'Game',
    // For the breadcrumbs bg pretty much
    highlight: nirvanaLogoYellow,
    highlight__color: nirvanaGray['10'],
    gap: nirvanaGap,
  },
  borderRadius: nirvanaRad,
  brand: nirvanaLogoYellow,
  brand__color: white,
  button: {
    backdrop: defaultLayerLight,
    canvas: nirvanaBtn,
    paper: defaultLayerLight,
    pasteboard: {},
  },
  canvas: {
    backgroundColor: white,
    borderRadius: nirvanaRad,
    fontFamily: 'Game',
    color: nirvanaGreen['90'],
    gap: nirvanaGapCanvas,
    highlight: nirvanaHighlight,
    opaque: nirvanaGreen['90'],
  },
  danger: appleRed,
  fontFamily: 'Play',
  href: nirvanaBlue['30'],
  input: {
    backdrop: nirvanaInputBackdropLight,
    canvas: { ...nirvanaInputLight, backgroundColor: nirvanaGray['30'] },
    paper: nirvanaInputLight,
    pasteboard: {},
  },
  paper: {
    backgroundColor: nirvanaGray['30'],
    borderRadius: nirvanaRad,
    highlight: nirvanaHighlight,
    color: nirvanaGray['50'],
    paddingHorizontal: nirvanaPadH,
    paddingVertical: nirvanaPadV,
    gap: nirvanaPadV,
  },
  pasteboard: pasteboardLayer,
  third: nirvanaBlue['30'],
  warning: nirvanaLogoYellow,
}
//#endregion nirvana
//#region ups
const upsBase: Layer = {
  borderWidth: 1,
  borderRadius: upsBorderRad,
  fontFamily: upsFontFamily,
  gap: 12,
  highlight: appleBlue,
  highlight__color: white,
}

const upsPaper: Layer = { ...upsBase, gap: 16 }

const upsPaperDark: Layer = {
  ...upsPaper,
  backgroundColor: upsDarkGrey,
  borderColor: appleCanvasDark,
  borderWidth: 2,
  color: white,
  faint: middleDarkGrey,
  separator: appleCanvasDark,
  opaque: appleCanvasDark,
  opaque__color: white,
}

const upsPaperLight: Layer = {
  ...upsPaper,
  backgroundColor: upsGrey,
  borderColor: upsBorder,
  borderWidth: 2,
  color: black,
  faint: nearWhite,
  separator: upsBorder,
  opaque: upsBorder,
  opaque__color: black,
}

const upsInput: Layer = {
  borderRadius: upsBorderRad,
  borderWidth: 1,
  faint: middleGrey,
  fontFamily: upsFontFamily,
  highlight: appleBlue,
  highlight__color: white,
  labelFontSize: 18,
  paddingHorizontal: 12,
  paddingVertical: 18,
  // focus: { borderColor: appleBlue },
}

const upsBlackBtn: Layer = {
  ...upsInput,
  backgroundColor: '#000',
  borderColor: '#000',
  color: white,
  faint: upsGreyHalf,
  faint__color: white,
  hoverBG: '#333',
}

const upsInputDark: Layer = {
  ...upsInput,
  backgroundColor: appleCanvasDark,
  borderColor: appleCanvasDark,
  color: white,
  faint: middleDarkGrey,
  labelColor: white,
  placeholderColor: middleDarkGrey,
}
//#endregion ups
export const themeTuples: Readonly<Record<string, ThemeTuple>> = {
  nirvana: {
    dark: nirvanaThemeDark,
    light: nirvanaThemeLight,
  },
  glow: {
    dark: {
      accent: glowLeaf,
      brand: glowSand,
      brand__color: appleRed,
      backdrop: { backgroundColor: white },
      button: {
        backdrop: {
          ...glowBtnBase,
        },
        canvas: {
          ...glowBtnBase,
        },
        paper: {
          ...glowBtnBase,
          ...glowPaperLight,
        },
        pasteboard: {},
      },
      canvas: glowPaperDark,
      danger: appleRed,
      fontFamily: 'Manrope',
      href: glowSand,
      input: {
        backdrop: {},
        canvas: {
          ...glowPaperLight,
          backgroundColor: white,
          color: black,
          paddingHorizontal: 18,
          paddingVertical: 12,
        },
        paper: {
          ...glowPaperLight,
          backgroundColor: white,
          color: black,
          paddingHorizontal: 18,
          paddingVertical: 12,
        },
        pasteboard: {},
      },
      paper: glowPaperDark,
      pasteboard: pasteboardLayer,
      third: '#9CC',
      warning: appleYellowFill,
    },
    light: {
      accent: glowSun,
      backdrop: { backgroundColor: white, separator: glowWetSand },
      borderRadius: glowBorderRad,
      brand: glowSand,
      brand__color: appleRed,
      button: {
        backdrop: { ...glowBtnBase },
        canvas: { ...glowBtnBase, color: glowTreeBark },
        paper: {
          ...glowBtnBase,
          backgroundColor: glowSand,
          borderColor: glowTreeBark,
          borderRadius: Number.MAX_SAFE_INTEGER,
          borderWidth: 1,
          color: glowTreeBark,
          highlight: appleBlue,
          hoverBG: glowWetSand,
        },
        pasteboard: {},
      },
      canvas: {
        borderRadius: glowBorderRad,
        backgroundColor: white,
        separator: glowTreeBark,
      },
      danger: appleRed,
      fontFamily: 'Manrope',
      href: appleBlue,
      input: {
        backdrop: {},
        canvas: {
          ...glowDefaultInputLight,
          backgroundColor: glowSand,
        },
        paper: glowDefaultInputLight,
        pasteboard: {},
      },
      paper: glowPaperLight,
      pasteboard: pasteboardLayer,
      third: '#C91',
      warning: appleYellowFill,
    },
  },
  upsmain: {
    dark: {
      ...upsBase,
      accent: glowSun,
      backdrop: {
        ...upsBase,
        backgroundColor: appleCanvasDark,
        borderColor: appleCanvasDark,
        color: white,
        highlight: appleBlue,
        separator: appleCanvasBorderDark,
      },
      borderRadius: upsBorderRad,
      brand: upsLeaf,
      brand__color: white,
      button: {
        backdrop: upsBlackBtn,
        canvas: upsBlackBtn,
        paper: {
          ...upsInput,
          color: white,
        },
        pasteboard: {},
      },
      canvas: {
        ...upsBase,
        backgroundColor: upsDarkGrey,
        borderColor: appleCanvasDark,
        borderRadius: upsBorderRad,
        borderWidth: 1,
        color: white,
        faint: middleDarkGrey,
        faint__color: midGrey,
        highlight: appleBlue,
        opaque: middleDarkGrey,
        separator: appleCanvasBorderDark,
      },
      danger: appleRed,
      href: appleBlue,
      input: {
        backdrop: upsInputDark,
        canvas: upsInputDark,
        paper: upsInputDark,
        pasteboard: {},
      },
      paper: upsPaperDark,
      pasteboard: pasteboardLayer,
      third: upsPurple,
      warning: '#440',
    },
    light: {
      ...upsBase,
      accent: glowSun,
      backdrop: {
        ...upsBase,
        backgroundColor: appleCanvas,
        borderColor: appleCanvas,
        color: black,
        highlight: appleBlue,
        separator: appleCanvasBorder,
      },
      brand: upsLeaf,
      brand__color: white,
      button: {
        backdrop: upsBlackBtn,
        canvas: upsBlackBtn,
        paper: {
          ...upsInput,
          borderColor: upsBorder,
          borderRadius: upsBorderRad,
          borderWidth: 1,
          hoverBG: upsPaperLight.faint,
        },
        pasteboard: {},
      },
      canvas: {
        ...upsBase,
        backgroundColor: white,
        borderColor: white,
        color: black,
        borderRadius: upsBorderRad,
        faint: upsGreyHalf,
        faint__color: grey.F,
        highlight: appleBlue,
        opaque: middleGrey,
        separator: nearWhite,
      },
      danger: appleRed,
      href: appleBlue,
      input: {
        backdrop: { ...upsInput, backgroundColor: black },
        canvas: {
          ...upsInput,
          backgroundColor: upsGrey,
          borderColor: upsGrey,
          color: black,
          placeholderColor: middleGrey,
        },
        paper: {
          ...upsInput,
          backgroundColor: white,
          borderColor: white,
          color: black,
          placeholderColor: middleGrey,
        },
        pasteboard: {},
      },
      paper: upsPaperLight,
      pasteboard: pasteboardLayer,
      third: upsPurple,
      warning: '#CCCC00',
    },
  },
}

//#endregion appColors
